import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check, Zap } from "lucide-react";
import { track } from "../../lib/api";

const STAGES = ["REQUIREMENT", "ARCHITECTURE", "AI-ASSISTED BUILD", "TEST", "DEPLOY", "LIVE APPLICATION"];
const CODE_LINES = [
  "$ b2l init --project client-portal",
  "▸ analysing requirement scope ...",
  "▸ scaffolding architecture ... done",
  "▸ generating auth + RBAC ... done",
  "▸ building dashboard modules ... done",
  "▸ wiring API integrations ... done",
  "▸ test suite: 42 passed, 0 failed",
  "▸ deploying to production ...",
  "✓ LIVE — handover: code + credentials",
];

function LiveBuildVisual() {
  const [stage, setStage] = useState(0);
  const [lines, setLines] = useState(0);

  useEffect(() => {
    const t = setInterval(() => {
      setLines((l) => {
        if (l >= CODE_LINES.length) {
          setStage(0);
          return 0;
        }
        return l + 1;
      });
      setStage((s) => Math.min(s + 1, STAGES.length - 1));
    }, 1100);
    return () => clearInterval(t);
  }, []);

  return (
    <div className="relative rounded-2xl border border-white/10 bg-navy-900/90 overflow-hidden" data-testid="hero-live-build-visual">
      <div className="flex items-center justify-between px-5 py-3 border-b border-white/10">
        <div className="flex items-center gap-2">
          <span className="w-2.5 h-2.5 rounded-full bg-red-500/70" />
          <span className="w-2.5 h-2.5 rounded-full bg-amber-500/70" />
          <span className="w-2.5 h-2.5 rounded-full bg-emerald-500/70" />
        </div>
        <div className="flex items-center gap-2 rounded-full border border-red-500/40 bg-red-500/10 px-3 py-1">
          <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse-dot" />
          <span className="font-mono text-[10px] font-bold tracking-[0.2em] text-red-400">LIVE</span>
        </div>
      </div>

      <div className="grid grid-cols-5 gap-0 border-b border-white/10">
        {STAGES.map((s, i) => (
          <div
            key={s}
            className={`px-2 py-3 text-center border-r border-white/5 last:border-r-0 transition-colors duration-500 ${
              i <= stage ? "bg-flame/10" : ""
            }`}
          >
            <p className={`font-mono text-[9px] sm:text-[10px] tracking-wider ${i <= stage ? "text-flame" : "text-slate-400"}`}>
              {i < stage ? "✓ " : i === stage ? "● " : ""}{s}
            </p>
          </div>
        ))}
      </div>

      <div className="p-5 h-64 overflow-hidden font-mono text-[11px] sm:text-xs leading-6">
        {CODE_LINES.slice(0, lines).map((l, i) => (
          <p key={i} className={l.startsWith("✓") ? "text-emerald-400" : l.startsWith("$") ? "text-slate-200" : "text-slate-500"}>
            <span className="text-slate-700 mr-2">{String(i).padStart(2, "0")}</span>{l}
          </p>
        ))}
        <span className="inline-block w-2 h-4 bg-flame animate-blink align-middle" />
      </div>
    </div>
  );
}

const TRUST = ["PREPAID DEVELOPMENT", "TRANSPARENT HOURLY PRICING", "SOURCE CODE HANDOVER", "SMEs & ENTERPRISES"];

export default function Hero() {
  return (
    <section className="relative pt-32 lg:pt-44 pb-20 lg:pb-28 overflow-hidden" data-testid="hero-section">
      <div
        className="absolute inset-0 bg-cover bg-center opacity-[0.14]"
        style={{ backgroundImage: "url('https://images.unsplash.com/photo-1534078362425-387ae9668c17?crop=entropy&cs=srgb&fm=jpg&q=85')" }}
      />
      <div className="absolute inset-0 bg-gradient-to-b from-navy/60 via-navy/85 to-navy" />

      <div className="container-x relative">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 lg:gap-20 items-center">
          <div>
            <div className="inline-flex items-center gap-2 rounded-full border border-flame/40 bg-flame/10 px-4 py-1.5 animate-fade-up">
              <span className="w-1.5 h-1.5 rounded-full bg-flame animate-pulse-dot" />
              <span className="font-mono text-[10px] font-bold tracking-[0.25em] text-flame">LIVE APPLICATION DEVELOPMENT</span>
            </div>

            <h1 className="mt-8 font-heading text-5xl sm:text-6xl lg:text-7xl font-black tracking-tighter leading-[0.95] text-white animate-fade-up" data-testid="hero-headline">
              BUILD SOFTWARE.<br />
              <span className="text-flame">LIVE.</span>
            </h1>

            <p className="mt-7 text-base sm:text-lg text-slate-300 leading-relaxed max-w-xl animate-fade-up" style={{ animationDelay: "0.1s" }}>
              Turn your business requirement into a working application — in days or, with Rush Hour, in hours.
            </p>
            <p className="mt-4 text-sm text-slate-400 leading-relaxed max-w-xl animate-fade-up" style={{ animationDelay: "0.15s" }}>
              Built2Last.live combines experienced software engineering with modern AI-assisted development workflows
              to build web, mobile and enterprise applications at unprecedented speed.
            </p>

            <div className="mt-10 flex flex-col sm:flex-row gap-4 animate-fade-up" style={{ animationDelay: "0.2s" }}>
              <Link
                to="/get-estimate"
                data-testid="hero-get-estimate-btn"
                onClick={() => track("estimate_click", { location: "hero" })}
                className="inline-flex items-center justify-center gap-2 rounded-full bg-flame hover:bg-flame-hover text-white font-heading font-bold tracking-wide px-8 py-4 transition-colors duration-200 shadow-[0_0_32px_rgba(249,115,22,0.4)]"
              >
                GET MY ESTIMATE <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                to="/book-rush-hour"
                data-testid="hero-book-rush-hour-btn"
                onClick={() => track("rush_hour_click", { location: "hero" })}
                className="inline-flex items-center justify-center gap-2 rounded-full border border-white/25 hover:border-flame hover:text-flame text-slate-100 font-heading font-bold tracking-wide px-8 py-4 transition-colors duration-200"
              >
                <Zap className="w-4 h-4" /> BOOK RUSH HOUR
              </Link>
            </div>

            <ul className="mt-12 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 animate-fade-up" style={{ animationDelay: "0.25s" }} data-testid="hero-trust-list">
              {TRUST.map((t) => (
                <li key={t} className="flex items-center gap-2.5 text-xs font-semibold tracking-wider text-slate-300">
                  <Check className="w-4 h-4 text-flame shrink-0" /> {t}
                </li>
              ))}
            </ul>
          </div>

          <div className="animate-fade-up" style={{ animationDelay: "0.2s" }}>
            <LiveBuildVisual />
            <p className="mt-4 text-center font-mono text-[10px] tracking-[0.3em] text-slate-500">
              YOU DESCRIBE IT. WE BUILD IT. YOU OWN IT.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
